import { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import { SearchX, Loader2, LayoutPanelLeft, AlignJustify, Columns, LayoutGrid, Square, SlidersHorizontal } from "lucide-react";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config.json";
import BiDashboard from "./CustomDashboard";
import { styles, PageSkeleton, emptyStateStyles } from "./helper";
import { sweetAlert } from "../../../../../utils/UniversalFunction";
import AnalyticsCard from "./components/AnalyticsCard";
import SummaryCard from "./components/SummaryCard";
import ExecSummaryBanner from "./components/ExecSummaryBanner";
import { insightsCache } from "./hooks/useInsights";

/* ─── Storage helpers ─────────────────────────────────────── */
const STORAGE_KEY_SUMMARY   = "cg_summary_cols";
const STORAGE_KEY_ANALYTICS = "cg_analytics_cols";
const readCols  = (key, fallback) => {
    try { const v = parseInt(localStorage.getItem(key), 10); return v > 0 ? v : fallback; }
    catch { return fallback; }
};
const saveCols = (key, val) => { try { localStorage.setItem(key, val); } catch {} };

/* ─── Column options ─────────────────────────────────────── */
const COL_OPTIONS = [
    { cols: 1, icon: AlignJustify, label: "1 per row" },
    { cols: 2, icon: Columns,      label: "2 per row" },
    { cols: 3, icon: LayoutGrid,   label: "3 per row" },
    { cols: 4, icon: Square,       label: "4 per row" },
];

const ColPicker = ({ value, onChange, accent = "#3b82f6" }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
        {COL_OPTIONS.map(({ cols, icon: Icon, label: tip }) => {
            const active = value === cols;
            return (
                <button key={cols} title={tip} onClick={() => onChange(cols)} style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 26, height: 26, borderRadius: 6, border: "none",
                    background: active ? accent : "transparent",
                    color: active ? "#fff" : "#cbd5e1",
                    cursor: "pointer", transition: "background 0.14s, color 0.14s",
                    outline: "none", flexShrink: 0,
                }}>
                    <Icon size={12} strokeWidth={active ? 2.3 : 1.7} />
                </button>
            );
        })}
    </div>
);

const colsToFlexStyle = (cols, total) => {
    if (cols === 1) return { width: "100%" };
    const pct = `calc(${(100 / cols).toFixed(4)}% - ${(20 * (cols - 1)) / cols}px)`;
    return { flex: `1 1 ${pct}`, maxWidth: total === 1 ? "100%" : pct, minWidth: "240px" };
};

const colsToGridStyle = (cols) => ({
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gap: "20px",
});

/* ─── Module-level preview cache ─────────────────────────────────────────────
   Keyed by String(graphId). Value is the resolved chartData object.
   Cleared on every full refresh (tab switch / refreshKey change).
   Individual entries are invalidated on sync/publish/delete.
─────────────────────────────────────────────────────────────────────────── */
const _previewCache = new Map();

const previewCache = {
    has:    (id) => _previewCache.has(String(id)),
    get:    (id) => _previewCache.get(String(id)),
    set:    (id, data) => _previewCache.set(String(id), data),
    delete: (id) => _previewCache.delete(String(id)),
    clear:  ()   => _previewCache.clear(),
};

/* ─── Memoized card wrappers ─────────────────────────────────────────────────
   Without memoization, every state change in CustomGraph (drag, toolbar,
   showInsights toggle, …) re-renders every card in the list.

   The comparator checks only props that actually affect the rendered output.
   Callback refs (onEdit, onSync, onDelete, …) are intentionally skipped —
   they're recreated each render but are cheap; including them would cause
   spurious re-renders because inline arrow functions always differ by reference.
─────────────────────────────────────────────────────────────────────────── */
const MemoAnalyticsCard = memo(AnalyticsCard, (prev, next) => {
    if (prev.data            !== next.data)            return false; // data arrived or synced
    if (prev.showInsights    !== next.showInsights)    return false; // toggle
    if (prev.syncing         !== next.syncing)         return false; // spinner
    if (prev.isDragging      !== next.isDragging)      return false; // this card being dragged
    if (prev.meta?.chartType           !== next.meta?.chartType)           return false;
    if (prev.meta?.representationType  !== next.meta?.representationType)  return false;
    if (prev.meta?.title               !== next.meta?.title)               return false;
    // widgetConfig covers group_by, filters, and all other config fields
    if (JSON.stringify(prev.meta?.widgetConfig) !== JSON.stringify(next.meta?.widgetConfig)) return false;
    return true; // nothing meaningful changed → skip re-render
});

const MemoSummaryCard = memo(SummaryCard, (prev, next) => {
    if (prev.data    !== next.data)    return false;
    if (prev.syncing !== next.syncing) return false;
    if (prev.meta?.title     !== next.meta?.title)     return false;
    if (prev.meta?.chartType !== next.meta?.chartType) return false;
    if (JSON.stringify(prev.meta?.widgetConfig) !== JSON.stringify(next.meta?.widgetConfig)) return false;
    return true;
});

/* ══════════════════════════════════════════════════════════════════════════ */
/*  CustomGraph                                                                */
/* ══════════════════════════════════════════════════════════════════════════ */
const CustomGraph = ({ tabName, searchTerm = "", refreshKey, registerPublishHandler }) => {
    /* ── Metadata ── */
    const [biGraphsMetadata, setBiGraphsMetadata] = useState([]);
    const [originalMetadata, setOriginalMetadata] = useState([]);
    const [metaLoading, setMetaLoading]           = useState(false);
    const [error, setError]                       = useState("");

    /* ── Preview data: graphId → chartData, populated one-by-one ── */
    const [dataMap, setDataMap] = useState({});

    /* ── Abort controller for the sequential fetch loop ── */
    const fetchAbortRef = useRef(null);

    /* ── Other UI ── */
    const [deleteConfirm, setDeleteConfirm] = useState({ show: false, graphId: null, graphQuery: "" });
    const [deleting, setDeleting]           = useState(null);
    const [showBiDashboard, setShowBiDashboard] = useState(false);
    const [editingGraph, setEditingGraph]   = useState(null);
    const [isDirty, setIsDirty]             = useState(false);
    const [isSaving, setIsSaving]           = useState(false);
    const [draggedItemId, setDraggedItemId] = useState(null);
    const [syncingIds, setSyncingIds]       = useState(new Set());

    /* ── Executive Summary ── */
    const [resolvedInsights, setResolvedInsights] = useState([]);
    // Incremented on delete so ExecSummaryBanner clears its stale cached summary
    const [summaryResetKey, setSummaryResetKey]   = useState(0);

    /* ── Layout ── */
    const [showLayoutControls, setShowLayoutControls] = useState(false);
    const [summaryCols, setSummaryCols]     = useState(() => readCols(STORAGE_KEY_SUMMARY, 3));
    const [analyticsCols, setAnalyticsCols] = useState(() => readCols(STORAGE_KEY_ANALYTICS, 2));
    const handleSummaryColsChange   = (v) => { setSummaryCols(v);   saveCols(STORAGE_KEY_SUMMARY, v); };
    const handleAnalyticsColsChange = (v) => { setAnalyticsCols(v); saveCols(STORAGE_KEY_ANALYTICS, v); };

    /* ── AI Insights toggle (default OFF) ── */
    const [showInsights, setShowInsights] = useState(false);
    const toggleInsights = () => setShowInsights(prev => !prev);

    /* ══════════════════════════════════════════════════════════
       Helper: shape raw preview API response into chartData
    ══════════════════════════════════════════════════════════ */
    const buildChartData = (meta, d) => ({
        data:         Array.isArray(d.data) ? d.data : [],
        chartType:    meta.chartType,
        xAxisField:   meta.widgetConfig?.group_by?.[0],
        stackByField: meta.widgetConfig?.group_by?.[1],
        title:        meta.title,
    });

    /* ══════════════════════════════════════════════════════════
       Helper: fetch preview for a single graph and populate dataMap.
       Used after publish / new-graph add when the caller doesn't
       already have chartData to hand in.
       Always bypasses the cache (caller must have a reason to fetch).
    ══════════════════════════════════════════════════════════ */
    const fetchSinglePreview = useCallback(async (meta) => {
        const id = String(meta.id);
        try {
            const { isSuccess: ok, data: d } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/preview`,
                {}, { ...meta }, "POST", false, false
            );
            if (ok && d?.data) {
                const chartData = buildChartData(meta, d);
                previewCache.set(id, chartData);
                setDataMap(prev => ({ ...prev, [id]: chartData }));
            }
        } catch {
            // card will stay in skeleton/empty state — no crash
        }
    }, []); // eslint-disable-line

    /* ══════════════════════════════════════════════════════════
       1. Fetch metadata → render all cards immediately (data = undefined)
          Then fetch each graph preview sequentially.
          Each card's data prop is set as soon as its fetch resolves.
          Cards for all representationTypes (Summary, Graph, Tabular) follow
          the same sequential pattern — no special-casing needed.
    ══════════════════════════════════════════════════════════ */
    const getMetadataList = useCallback(async () => {
        // Cancel any in-progress sequential loop from a prior call
        if (fetchAbortRef.current) fetchAbortRef.current.abort();
        const ac = new AbortController();
        fetchAbortRef.current = ac;

        setMetaLoading(true);
        setError("");
        setResolvedInsights([]);
        setDataMap({});
        previewCache.clear(); // stale data from prior tab must not persist

        try {
            const { isSuccess, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/getMetaData`,
                {},
                { module: tabName?.replace(/\s*&\s*/g, "&") },
                "GET"
            );
            if (!isSuccess || !Array.isArray(data)) { setMetaLoading(false); return; }

            const sorted = [...data].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            setBiGraphsMetadata(sorted);
            setOriginalMetadata(JSON.parse(JSON.stringify(sorted)));

            // Metadata is ready — all cards render immediately with data=undefined
            // (AnalyticsCard/SummaryCard show their own skeleton while data is absent)
            setMetaLoading(false);

            // Sequential preview fetches — one at a time in display order
            for (const meta of sorted) {
                if (ac.signal.aborted) break;

                const id = String(meta.id);

                // Cache hit: hydrate card instantly, no network call
                if (previewCache.has(id)) {
                    setDataMap(prev => ({ ...prev, [id]: previewCache.get(id) }));
                    continue;
                }

                try {
                    const { isSuccess: ok, data: d } = await apiCall(
                        `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/preview`,
                        {}, { ...meta }, "POST", false, false
                    );
                    if (ac.signal.aborted) break;

                    if (ok && d?.data) {
                        const chartData = buildChartData(meta, d);
                        previewCache.set(id, chartData);
                        setDataMap(prev => ({ ...prev, [id]: chartData }));
                    }
                    // On individual failure: card stays in skeleton/empty state — no crash
                } catch {
                    // swallow per-graph errors; card handles its own empty state
                }
            }
        } catch {
            setError("Failed to fetch analytics definitions");
            setMetaLoading(false);
        }
    }, [tabName]); // eslint-disable-line

    useEffect(() => {
        setResolvedInsights([]);
        setDataMap({});
        getMetadataList();
    }, [getMetadataList, refreshKey]);

    // Abort the sequential loop on unmount
    useEffect(() => () => { fetchAbortRef.current?.abort(); }, []);

    /* ── 2. Publish handler ──
       Called by BiDashboard when a graph is saved/published.
       - If chartData is provided: update cache + dataMap directly (zero extra fetch).
       - If chartData is absent (graph was saved but preview wasn't returned):
         invalidate the cache entry and kick off a single preview fetch so only
         that one card re-hydrates — all other cards are untouched.
    ── */
    const updateOnPublish = useCallback((updatedGraph, updatedGraphData) => {
        if (!updatedGraph?.id) return;
        const id = String(updatedGraph.id);

        // 1. Merge metadata (title, chartType, widgetConfig, representationType, etc.)
        setBiGraphsMetadata(prev => {
            const index = prev.findIndex(g => String(g.id) === id);
            if (index === -1) return [...prev, updatedGraph];
            const next = [...prev];
            next[index] = { ...prev[index], ...updatedGraph, representationType: updatedGraph.representationType };
            return next;
        });

        // 2. Evict stale insights so useInsights re-fetches with the new config/data.
        //    Also remove the resolvedInsights entry — handleInsightsReady will re-add
        //    it (with the correct new title) once the fresh insight resolves.
        insightsCache.clear(tabName, id);
        setResolvedInsights(prev => prev.filter(r => String(r.graphId) !== id));

        // 3. Hydrate preview data
        if (updatedGraphData) {
            previewCache.set(id, updatedGraphData);
            setDataMap(prev => ({ ...prev, [id]: updatedGraphData }));
        } else {
            previewCache.delete(id);
            fetchSinglePreview(updatedGraph);
        }
    }, [fetchSinglePreview, tabName]);

    useEffect(() => {
        if (!registerPublishHandler) return;
        registerPublishHandler(tabName, updateOnPublish);
        return () => registerPublishHandler(tabName, null);
    }, [tabName, registerPublishHandler, updateOnPublish]);

    /* ── 3. onInsightsReady (from each AnalyticsCard via useInsights) ──
       Deduplicates by graphId so a title change on edit never leaves a
       ghost entry in resolvedInsights under the old title.
    ── */
    const handleInsightsReady = useCallback(({ graphId, title, narrative }) => {
        setResolvedInsights(prev => {
            const id = String(graphId);
            const exists = prev.findIndex(r => String(r.graphId) === id);
            if (exists > -1) {
                const next = [...prev];
                next[exists] = { graphId: id, title, narrative };
                return next;
            }
            return [...prev, { graphId: id, title, narrative }];
        });
    }, []);

    /* ── 4. Filtering ── */
    const filteredMetadata = useMemo(() => {
        if (!searchTerm) return biGraphsMetadata;
        return biGraphsMetadata.filter(g => g.title?.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [biGraphsMetadata, searchTerm]);

    const summaryMetadata   = filteredMetadata.filter(g => g.representationType === "Summary");
    const visualMetadata    = filteredMetadata.filter(g => g.representationType !== "Summary");
    const chartOnlyMetadata = visualMetadata.filter(g => g.representationType !== "Tabular");

    /* ── 5. Drag & Drop ── */
    const handleDragStart = (e, graphId) => { setDraggedItemId(graphId); e.dataTransfer.effectAllowed = "move"; };
    const handleDragOver  = (e) => { e.preventDefault(); };
    const handleDrop = (e, targetId) => {
        e.preventDefault();
        if (!draggedItemId || draggedItemId === targetId) { setDraggedItemId(null); return; }
        const newMeta = [...biGraphsMetadata];
        const dIdx = newMeta.findIndex(g => g.id === draggedItemId);
        const tIdx = newMeta.findIndex(g => g.id === targetId);
        if (dIdx !== -1 && tIdx !== -1) {
            const [item] = newMeta.splice(dIdx, 1);
            newMeta.splice(tIdx, 0, item);
            setBiGraphsMetadata(newMeta);
            setIsDirty(true);
        }
        setDraggedItemId(null);
    };

    const handleSaveOrder = async () => {
        setIsSaving(true);
        try {
            const graphOrders = biGraphsMetadata.map((g, i) => ({ id: String(g.id), displayOrder: i }));
            const { isSuccess } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}bi-graphs/reorder`, {}, { graphOrders }, "POST"
            );
            if (isSuccess) {
                setOriginalMetadata(JSON.parse(JSON.stringify(biGraphsMetadata)));
                setIsDirty(false);
                sweetAlert("success", "New layout saved", false);
            }
        } catch { setError("Save reorder failed"); }
        finally { setIsSaving(false); }
    };

    const handleDashboardClose = useCallback((shouldRefresh = true, updatedGraph = null) => {
        setShowBiDashboard(false);
        setEditingGraph(null);

        if (updatedGraph?.id) {
            const targetId = String(updatedGraph.id);

            setBiGraphsMetadata(prev => {
                const idx = prev.findIndex(g => String(g.id) === targetId);
                if (idx > -1) {
                    // Edit — merge metadata only
                    const n = [...prev];
                    n[idx] = { ...prev[idx], ...updatedGraph, representationType: updatedGraph.representationType };
                    return n;
                }
                // New graph — append
                return [...prev, updatedGraph];
            });

            // Evict stale insight so useInsights re-fetches with new config/data.
            // resolvedInsights entry is removed here; handleInsightsReady re-adds it
            // with the correct (possibly new) title once the fresh insight resolves.
            insightsCache.clear(tabName, targetId);
            setResolvedInsights(prev => prev.filter(r => String(r.graphId) !== targetId));

            if (updatedGraph.chartData) {
                previewCache.set(targetId, updatedGraph.chartData);
                setDataMap(prev => ({ ...prev, [targetId]: updatedGraph.chartData }));
            } else {
                previewCache.delete(targetId);
                fetchSinglePreview(updatedGraph);
            }
            return;
        }

        if (shouldRefresh) { getMetadataList(); setDataMap({}); }
    }, [fetchSinglePreview, getMetadataList, tabName]);

    /* ── Sync single graph — bypasses + updates cache ── */
    const syncSingleGraph = async (graphId) => {
        if (syncingIds.has(graphId)) return;
        setSyncingIds(prev => new Set(prev).add(graphId));
        try {
            const { isSuccess, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/${graphId}/sync`, {}, {}, "GET"
            );
            if (isSuccess && data) {
                previewCache.set(String(graphId), data);
                setDataMap(prev => ({ ...prev, [graphId]: data }));
            }
        } catch { sweetAlert("error", "Failed to sync graph", false); }
        finally { setSyncingIds(prev => { const n = new Set(prev); n.delete(graphId); return n; }); }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirm.graphId) return;
        try {
            setDeleting(deleteConfirm.graphId);
            const { isSuccess } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/${deleteConfirm.graphId}`, {}, {}, "DELETE"
            );
            if (isSuccess) {
                const deletedId = String(deleteConfirm.graphId);
                previewCache.delete(deletedId);
                insightsCache.clear(tabName, deletedId);
                setBiGraphsMetadata(prev => prev.filter(g => g.id !== deleteConfirm.graphId));
                setDataMap(prev => { const n = { ...prev }; delete n[deleteConfirm.graphId]; return n; });
                // Remove this graph's insight from resolvedInsights so ExecSummaryBanner
                // recalculates with the updated set of narratives
                setResolvedInsights(prev => prev.filter(r => String(r.graphId) !== deletedId));
                // Bump resetKey so ExecSummaryBanner clears its stale cached summary
                setSummaryResetKey(k => k + 1);
                setDeleteConfirm({ show: false, graphId: null, graphQuery: "" });
                sweetAlert("success", "Deleted successfully", false);
            }
        } finally { setDeleting(null); }
    };

    const hasSummary   = summaryMetadata.length > 0;
    const hasAnalytics = visualMetadata.length > 0;

    return (
        <div style={{ ...styles.container, overflowX: "hidden", boxSizing: "border-box" }}>
            <div style={{ ...styles.contentWrapper, boxSizing: "border-box", width: "100%", maxWidth: "100%" }}>
                {error && <div className="alert alert-danger">{error}</div>}

                {metaLoading && biGraphsMetadata.length === 0 ? <PageSkeleton /> : (
                    <>
                        {/* Empty states */}
                        {searchTerm && filteredMetadata.length === 0 && !metaLoading && (
                            <div style={emptyStateStyles.container}>
                                <div style={emptyStateStyles.iconCircle}><SearchX size={28} color="#64748b" /></div>
                                <h3 style={emptyStateStyles.title}>No matches found</h3>
                                <p style={emptyStateStyles.text}>
                                    We couldn't find any analytics matching "<strong>{searchTerm}</strong>". Try a different term.
                                </p>
                            </div>
                        )}

                        {!searchTerm && biGraphsMetadata.length === 0 && !metaLoading && (
                            <div style={emptyStateStyles.container}>
                                <div style={emptyStateStyles.iconCircle}><LayoutPanelLeft size={48} color="#3b82f6" /></div>
                                <h3 style={emptyStateStyles.title}>No Analytics in {tabName}</h3>
                                <p style={emptyStateStyles.text}>
                                    You haven't published any custom graphs for this module yet.
                                </p>
                                <button className="btn btn-primary" style={{ marginTop: "20px" }} onClick={() => setShowBiDashboard(true)}>
                                    Create
                                </button>
                            </div>
                        )}

                        {/* ── Toolbar: Layout + AI Insights toggle ── */}
                        {!searchTerm && (hasSummary || hasAnalytics) && (
                            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 10 }}>
                                {/* AI Insights toggle */}
                                <button
                                    title={showInsights ? "Hide AI insights & summary" : "Show AI insights & summary"}
                                    onClick={toggleInsights}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 6, padding: "4px 11px",
                                        borderRadius: 7,
                                        border: showInsights ? "1px solid #7c3aed30" : "1px solid #e2e8f0",
                                        background: showInsights ? "#faf5ff" : "transparent",
                                        color: showInsights ? "#7c3aed" : "#94a3b8",
                                        fontSize: 11, fontWeight: 600,
                                        fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em",
                                        cursor: "pointer", transition: "all 0.15s", outline: "none",
                                    }}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                                        strokeLinecap="round" strokeLinejoin="round"
                                        style={{ width: 12, height: 12, minWidth: 12, display: "block" }}>
                                        <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
                                    </svg>
                                    AI Insights
                                    <span style={{
                                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                                        width: 28, height: 15, borderRadius: 99,
                                        background: showInsights ? "#7c3aed" : "#cbd5e1",
                                        transition: "background 0.2s", position: "relative", flexShrink: 0,
                                    }}>
                                        <span style={{
                                            width: 11, height: 11, borderRadius: "50%", background: "white",
                                            position: "absolute",
                                            left: showInsights ? "calc(100% - 13px)" : 2,
                                            transition: "left 0.2s",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                                        }} />
                                    </span>
                                </button>

                                {/* Layout button */}
                                <button
                                    title={showLayoutControls ? "Hide layout controls" : "Customize layout"}
                                    onClick={() => setShowLayoutControls(v => !v)}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 5, padding: "4px 10px",
                                        borderRadius: 7,
                                        border: showLayoutControls ? "1px solid #3b82f620" : "1px solid #e2e8f0",
                                        background: showLayoutControls ? "#eff6ff" : "transparent",
                                        color: showLayoutControls ? "#3b82f6" : "#94a3b8",
                                        fontSize: 11, fontWeight: 600,
                                        fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em",
                                        cursor: "pointer", transition: "all 0.15s", outline: "none",
                                    }}
                                >
                                    <SlidersHorizontal size={12} strokeWidth={2} />
                                    Layout
                                </button>
                            </div>
                        )}

                        {/* ── Summary layout picker ── */}
                        {showLayoutControls && hasSummary && (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, marginBottom: 8 }}>
                                <span style={{ fontSize: 10, color: "#94a3b8", fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                                    Summary
                                </span>
                                <ColPicker value={summaryCols} onChange={handleSummaryColsChange} accent="#7c3aed" />
                            </div>
                        )}

                        {/* ── Summary cards ── */}
                        {hasSummary && (
                            <div style={{ marginBottom: "28px" }}>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", transition: "all 0.25s ease" }}>
                                    {summaryMetadata.map(meta => (
                                        <div key={`sum-${meta.id}`} style={{ ...colsToFlexStyle(summaryCols, summaryMetadata.length), transition: "all 0.25s ease" }}>
                                            <MemoSummaryCard
                                                meta={meta}
                                                data={dataMap[meta.id]?.data}
                                                syncing={syncingIds.has(meta.id)}
                                                onEdit={() => { setEditingGraph({ ...meta, chartData: dataMap[meta.id] }); setShowBiDashboard(true); }}
                                                onSync={() => syncSingleGraph(meta.id)}
                                                onDelete={() => setDeleteConfirm({ show: true, graphId: meta.id, graphQuery: meta.title })}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ══ Executive Summary Banner — only when AI insights are on ══ */}
                        {!searchTerm && showInsights && chartOnlyMetadata.length > 0 && (
                            <ExecSummaryBanner
                                resolvedInsights={resolvedInsights}
                                totalGraphCount={chartOnlyMetadata.length}
                                tabName={tabName}
                                resetKey={summaryResetKey}
                            />
                        )}

                        {/* ── Charts layout picker — only when AI insights off ── */}
                        {showLayoutControls && hasAnalytics && !showInsights && (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, marginBottom: 8 }}>
                                <span style={{ fontSize: 10, color: "#94a3b8", fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                                    Charts
                                </span>
                                <ColPicker value={analyticsCols} onChange={handleAnalyticsColsChange} accent="#3b82f6" />
                            </div>
                        )}

                        {/* ── Analytics cards ──
                            All cards render as soon as metadata loads.
                            `data` prop is undefined until that card's sequential preview fetch
                            resolves — the card shows its own skeleton/spinner meanwhile.

                            showInsights ON  → single-column flex stack (50/50 chart+insights)
                            showInsights OFF → CSS grid with user-chosen column count
                        ── */}
                        {hasAnalytics && (
                            <div style={
                                showInsights
                                    ? { display: "flex", flexDirection: "column", gap: 20 }
                                    : { ...colsToGridStyle(analyticsCols), transition: "all 0.25s ease" }
                            }>
                                {visualMetadata.map(meta => (
                                    <MemoAnalyticsCard
                                        key={meta.id}
                                        meta={meta}
                                        data={dataMap[meta.id]}
                                        styles={styles}
                                        syncing={syncingIds.has(meta.id)}
                                        isDragging={draggedItemId === meta.id}
                                        onDragStart={(e) => handleDragStart(e, meta.id)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, meta.id)}
                                        onEdit={() => { setEditingGraph({ ...meta, chartData: dataMap[meta.id] }); setShowBiDashboard(true); }}
                                        onSync={() => syncSingleGraph(meta.id)}
                                        onDelete={() => setDeleteConfirm({ show: true, graphId: meta.id, graphQuery: meta.title })}
                                        onInsightsReady={handleInsightsReady}
                                        showInsights={showInsights}
                                        tabName={tabName}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Save order */}
            {isDirty && (
                <div style={styles.saveButtonContainer}>
                    <button style={styles.saveButton} onClick={handleSaveOrder} disabled={isSaving}>
                        {isSaving ? "Saving..." : "✓ Save Order"}
                    </button>
                    <button style={styles.cancelButton} onClick={() => { setBiGraphsMetadata(JSON.parse(JSON.stringify(originalMetadata))); setIsDirty(false); }}>
                        Cancel
                    </button>
                </div>
            )}

            {showBiDashboard && (
                <BiDashboard isOpen={showBiDashboard} editingGraph={editingGraph} onClose={handleDashboardClose} onPublish={updateOnPublish} />
            )}

            {deleteConfirm.show && (
                <div style={styles.modalOverlay}>
                    <div style={{ background: "white", padding: "30px", borderRadius: "15px", maxWidth: "400px", width: "90%" }}>
                        <h5>Delete Graph?</h5>
                        <p>Are you sure you want to delete <strong>{deleteConfirm.graphQuery}</strong>?</p>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                            <button className="btn btn-secondary" onClick={() => setDeleteConfirm({ show: false })}>Cancel</button>
                            <button className="btn btn-danger" onClick={handleDeleteConfirm} disabled={deleting !== null}>
                                {deleting ? "Deleting..." : "Confirm Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomGraph;